import type { CSSObject, Theme } from '@mui/material/styles';

import { withAlpha } from 'utils/colorUtils';

// ==============================|| SIDEBAR - ESTILOS COMPARTILHADOS (BONESIDE) ||============================== //
// Centraliza a identidade visual do menu lateral pra manter NavItem, NavCollapse e os dois
// drawers (desktop mini + mobile temporário) coerentes. Cores por CSS var (theme.vars) e
// diferenças de tema via theme.applyStyles('dark', …) — nunca theme.palette.mode em styled/sx
// (congelaria no tema default).

// Cores da superfície (gradientes custom — não cabem num único token; centralizadas aqui).
// O brilho slate do topo vem do próprio primary.main via withAlpha (não hardcode do valor da marca).
const SURFACE_LIGHT = { top: '#ECECEC', bottom: '#DBDBDB' };
const SURFACE_DARK = { top: '#1E2C38', mid: '#17222C', bottom: '#131D26' };
const MUTED_ICON_LIGHT = '#6E7E8C'; // ícone inativo no light (>= AA de UI vs painel claro)

// Superfície do sidebar — dá "presença" ao painel nos dois temas.
// Light: gradiente slate frio (destaca do conteúdo quase-branco) + brilho da marca no topo → leve, mas presente.
// Dark: navy em degradê (mais profundo que os cards) + brilho slate no topo → profundidade sem virar bloco.
export function sidebarSurface(theme: Theme): CSSObject {
  const glow = (alpha: number) => withAlpha(theme.vars!.palette.primary.main, alpha);
  return {
    backgroundColor: theme.vars!.palette.background.paper,
    // Light: painel slate frio quase-flat (limpo, sem o brilho radial que "mancha" no claro) + borda nítida = divisão clara.
    backgroundImage: `linear-gradient(180deg, ${SURFACE_LIGHT.top} 0%, ${SURFACE_LIGHT.bottom} 100%)`,
    // Sem linha dura: anula a borda padrão do MUI e usa sombra suave (efeito premium de "flutuar" sobre o conteúdo).
    borderRight: 'none',
    boxShadow: '2px 0 5px rgba(19, 29, 38, 0.06), 10px 0 24px -6px rgba(19, 29, 38, 0.10)',
    ...theme.applyStyles('dark', {
      backgroundImage: `radial-gradient(140% 70% at 50% -20%, ${glow(0.22)}, transparent 60%), linear-gradient(180deg, ${SURFACE_DARK.top} 0%, ${SURFACE_DARK.mid} 55%, ${SURFACE_DARK.bottom} 100%)`,
      borderRight: `1px solid ${withAlpha('#55626E', 0.42)}`,
      boxShadow: '4px 0 24px rgba(0, 0, 0, 0.32)'
    })
  };
}

type NavRowOptions = { open: boolean; level: number; active: boolean };

// Estilo base de uma linha do menu (item ou grupo): pílula inset, hover suave com nudge do ícone
// e estado ativo com gradiente da marca. `open` = linha em layout expandido (drawer aberto OU
// item aninhado que sempre mostra rótulo).
export function navItemSx(theme: Theme, { open, level, active }: NavRowOptions): CSSObject {
  // Hover mais evidente quando recolhido (só ícone) — no aberto fica sutil.
  const hoverBgLight = withAlpha(theme.vars!.palette.primary.main, open ? 0.08 : 0.12);
  const hoverBgDark = withAlpha(theme.vars!.palette.primary.light, open ? 0.14 : 0.18);

  const activeBgLight = `linear-gradient(90deg, ${withAlpha(theme.vars!.palette.primary.main, 0.2)} 0%, ${withAlpha(theme.vars!.palette.primary.main, 0.06)} 100%)`;
  const activeHoverLight = `linear-gradient(90deg, ${withAlpha(theme.vars!.palette.primary.main, 0.24)} 0%, ${withAlpha(theme.vars!.palette.primary.main, 0.08)} 100%)`;
  const activeBgDark = `linear-gradient(90deg, ${withAlpha(theme.vars!.palette.primary.light, 0.3)} 0%, ${withAlpha(theme.vars!.palette.primary.light, 0.08)} 100%)`;
  const activeHoverDark = `linear-gradient(90deg, ${withAlpha(theme.vars!.palette.primary.light, 0.34)} 0%, ${withAlpha(theme.vars!.palette.primary.light, 0.1)} 100%)`;

  const iconTransform = open ? 'translateX(2px)' : 'none';

  return {
    position: 'relative',
    marginInline: open ? theme.spacing(1) : theme.spacing(0.75),
    marginBlock: theme.spacing(0.25),
    borderRadius: 8,
    minHeight: 40,
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: open ? `${(level - 1) * 20 + 12}px` : 0,
    paddingRight: open ? theme.spacing(1) : 0,
    justifyContent: open ? 'flex-start' : 'center',
    color: theme.vars!.palette.text.primary,
    ...(active && { backgroundImage: activeBgLight }),
    transition: theme.transitions.create(['background-color', 'background-image', 'color'], {
      duration: theme.transitions.duration.shorter
    }),
    '& .MuiListItemIcon-root': {
      // Inativo no light: MUTED_ICON_LIGHT (>= AA de UI vs painel claro; o antigo text.secondary #8c8c8c ficava 2.96).
      // Dark mantém text.secondary (#B1B1B1) via applyStyles abaixo.
      color: active ? theme.vars!.palette.primary.main : MUTED_ICON_LIGHT,
      transition: theme.transitions.create(['color', 'transform'], { duration: theme.transitions.duration.shorter })
    },
    '&:hover': {
      backgroundColor: active ? undefined : hoverBgLight,
      backgroundImage: active ? activeHoverLight : undefined,
      '& .MuiListItemIcon-root': {
        color: active ? theme.vars!.palette.primary.main : theme.vars!.palette.text.primary,
        transform: iconTransform
      }
    },
    '&.Mui-disabled': { opacity: 0.4 },
    // Foco de teclado visível (WCAG 2.4.7) — anel da marca acompanhando a pílula.
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.vars!.palette.primary.main}`,
      outlineOffset: '-2px'
    },
    // Respeita "reduzir movimento": sem nudge do ícone nem transições.
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
      '& .MuiListItemIcon-root': { transition: 'none' },
      '&:hover .MuiListItemIcon-root': { transform: 'none' }
    },
    ...theme.applyStyles('dark', {
      ...(active && { backgroundImage: activeBgDark }),
      '&.Mui-focusVisible': { outline: `2px solid ${theme.vars!.palette.primary.light}` },
      '& .MuiListItemIcon-root': {
        color: active ? theme.vars!.palette.primary.light : theme.vars!.palette.text.secondary
      },
      '&:hover': {
        backgroundColor: active ? undefined : hoverBgDark,
        backgroundImage: active ? activeHoverDark : undefined,
        '& .MuiListItemIcon-root': {
          color: active ? theme.vars!.palette.primary.light : theme.vars!.palette.text.primary,
          transform: iconTransform
        }
      }
    })
  };
}

// Barra de acento do item ativo — na borda esquerda do rail, com glow discreto da marca.
export function navAccentSx(theme: Theme, open: boolean): CSSObject {
  return {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: open ? 24 : 26,
    borderRadius: '0 3px 3px 0',
    backgroundColor: theme.vars!.palette.primary.main,
    boxShadow: `0 0 8px ${withAlpha(theme.vars!.palette.primary.main, 0.5)}`,
    pointerEvents: 'none',
    zIndex: 2,
    ...theme.applyStyles('dark', {
      backgroundColor: theme.vars!.palette.primary.light,
      boxShadow: `0 0 10px ${withAlpha(theme.vars!.palette.primary.light, 0.55)}`
    })
  };
}
